import { camelCase } from 'lodash'

import { EAgentKeys, TAgent } from '~/features/agents/models'
import {
  ECampaignBuilderFormKeys,
  ECampaignKeys,
  TCampaign,
  TCampaignAgentPayload,
  TCampaignBuilderForm,
} from '~/features/campaigns/models'
import { getCampaignAssistantJunctions } from '~/features/campaigns/utils/campaignAssistants'

export function getAgentMetadataVariable(agentMetadataKey: string): string {
  return camelCase(agentMetadataKey || '')
}

export const getValueByNormalizedVariable = (
  source: Record<string, unknown> | null | undefined,
  variable: string,
): string => {
  const normalizedVariable = getAgentMetadataVariable(variable)
  if (!normalizedVariable) {
    return ''
  }

  const directValue = String(source?.[normalizedVariable] ?? '').trim()
  if (directValue) {
    return directValue
  }

  const matchedEntry = Object.entries(source ?? {}).find(([key, value]) => {
    if (getAgentMetadataVariable(key) !== normalizedVariable) {
      return false
    }
    return Boolean(String(value ?? '').trim())
  })
  if (matchedEntry) {
    return String(matchedEntry[1] ?? '').trim()
  }

  return ''
}

/**
 * Fills a campaign's agent metadata variables from the assistant record.
 *
 * `metadataVariables` is every variable the campaign declares, required or not:
 * an optional variable left blank still renders as a blank in the outreach copy,
 * so whenever an assistant is attached to a campaign we fill everything the
 * assistant record can supply. Values already present win over derived ones.
 */
export function resolveAutoFilledAgentMetadata({
  existingLocationFields,
  assistantRecord,
  metadataVariables,
}: {
  existingLocationFields?: Record<string, string | null | undefined>
  assistantRecord?: Record<string, unknown> | null
  metadataVariables: string[]
}): Record<string, string> {
  const normalizedExistingLocationFields = Object.fromEntries(
    Object.entries(existingLocationFields || {}).flatMap(
      ([fieldKey, fieldValue]) => {
        const variableKey = getAgentMetadataVariable(String(fieldKey))
        if (!variableKey) {
          return []
        }
        const normalizedValue = String(fieldValue ?? '').trim()
        if (!normalizedValue) {
          return []
        }
        return [[variableKey, normalizedValue]]
      },
    ),
  ) as Record<string, string>

  return Object.fromEntries(
    metadataVariables.flatMap((variable) => {
      if (!variable) {
        return []
      }

      const existingValue = getValueByNormalizedVariable(
        normalizedExistingLocationFields,
        variable,
      )
      if (existingValue) {
        return [[variable, existingValue]]
      }

      const assistantValue = getValueByNormalizedVariable(
        assistantRecord || {},
        variable,
      )
      if (!assistantValue) {
        return []
      }

      return [[variable, assistantValue]]
    }),
  ) as Record<string, string>
}

export function appendRequiredAgentMetadataToPayload({
  agents = [],
  campaign,
  payload,
  requiredMetadataVariables,
  values,
}: {
  agents?: TAgent[]
  campaign?: TCampaign | null
  payload: Record<string, unknown>
  requiredMetadataVariables: string[]
  values: TCampaignBuilderForm
}): Record<string, unknown> {
  if (!requiredMetadataVariables.length) {
    return payload
  }

  const sharedAgentIds = (
    values[ECampaignBuilderFormKeys.sharedCampaignAgentIds] ?? []
  ).map(String)
  if (!sharedAgentIds.length) {
    return payload
  }

  const sharedAgentSettings =
    values[ECampaignBuilderFormKeys.sharedCampaignAgentSettings] ?? {}
  const agentById = new Map(
    (agents || []).map((agent) => [String(agent[EAgentKeys.id] || ''), agent]),
  )
  const junctionByAgentId = new Map(
    getCampaignAssistantJunctions(campaign).map((junction) => [
      String(junction.agentId || junction.Agent?.id || ''),
      junction,
    ]),
  )

  const metadataUpdates = sharedAgentIds.flatMap((agentId) => {
    const junction = junctionByAgentId.get(agentId)
    const assistantRecord =
      agentById.get(agentId) ||
      ((junction?.Agent || null) as Record<string, unknown> | null)
    const metadata = resolveAutoFilledAgentMetadata({
      existingLocationFields:
        sharedAgentSettings[agentId]?.locationFields ?? {},
      assistantRecord,
      metadataVariables: requiredMetadataVariables,
    })

    if (!Object.keys(metadata).length) {
      return []
    }

    return [{ agentId, metadata }]
  })

  if (!metadataUpdates.length) {
    return payload
  }

  const nextPayload = { ...payload }
  const existingAgents = new Map(
    ((nextPayload[ECampaignKeys.agents] as TCampaignAgentPayload[]) || []).map(
      (agentPayload) => [String(agentPayload.agentId), agentPayload],
    ),
  )

  metadataUpdates.forEach(({ agentId, metadata }) => {
    const existingAgentPayload = existingAgents.get(agentId)
    existingAgents.set(agentId, {
      ...existingAgentPayload,
      agentId,
      agentMetadata: {
        ...(existingAgentPayload?.agentMetadata || {}),
        ...metadata,
      },
    })
  })

  nextPayload[ECampaignKeys.agents] = Array.from(existingAgents.values())
  return nextPayload
}
